import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  TableSortLabel,
  Typography,
} from '@mui/material';

// Column type rendering
export type ColumnType =
  | 'default'
  | 'chip'
  | 'patient'
  | 'action'
  | 'mono'
  | 'actionButton'
  | 'issues';

export interface ColumnDef {
  key: string;
  label: string;
  width: string; // CSS grid width e.g. '80px', '1.2fr', '2fr'
  type?: ColumnType;
  align?: 'left' | 'right' | 'center';
  required?: boolean;
  /** Enables the optional table-header sorting interaction for this column. */
  sortable?: boolean;
  /** Direction to use when this column becomes the active sort. */
  defaultSortDirection?: 'asc' | 'desc';
}

export type DataTableSort = {
  columnKey: string;
  direction: 'asc' | 'desc';
  onChange: (columnKey: string, direction: 'asc' | 'desc') => void;
};

// Row data is inherently dynamic — columns are user-configurable
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RowData = Record<string, any>;

interface DataTableProps {
  columns: ColumnDef[];
  rows: RowData[];
  onRowClick?: (row: RowData) => void;
  onAction?: (row: RowData) => void;
  idKey?: string;
  isFetching?: boolean;
  /** Render a custom element at the end of each row (e.g., action menu) */
  renderRowEnd?: (row: RowData) => React.ReactNode;
  /** Optional label and width for a multi-control row action area. */
  rowEndLabel?: string;
  rowEndWidth?: string;
  /** Enable row selection with checkboxes */
  selectedIds?: Set<string>;
  onSelectionChange?: (ids: Set<string>) => void;
  /** Optional row-level selection gate. Defaults to all rows selectable. */
  isRowSelectable?: (row: RowData) => boolean;
  /** Accessible label for the header selection control. */
  selectionLabel?: string;
  /** Accessible label for an individual row selection control. */
  getRowSelectionLabel?: (row: RowData) => string;
  /** Override cell rendering for specific columns. Return null to fall back to default. */
  renderCell?: (column: ColumnDef, row: RowData) => React.ReactNode | null;
  /** Compute custom sx overrides per row (e.g., opacity for dimmed rows) */
  rowSx?: (row: RowData) => Record<string, unknown>;
  /** Optional controlled sort state. Existing tables remain unchanged when omitted. */
  sort?: DataTableSort;
}

const chipColorMap: Record<string, { bg: string; text: string; border: string }> = {
  drafts: { bg: '#FFFBEB', text: '#92400E', border: '#FDE68A' },
  ready: { bg: '#F0FDF4', text: '#166534', border: '#BBF7D0' },
  inFlight: { bg: '#F5F4FB', text: '#36229E', border: '#E6E1FF' },
  paid: { bg: '#F0FDF4', text: '#166534', border: '#BBF7D0' },
  review: { bg: '#FEF2F2', text: '#991B1B', border: '#FECACA' },
  clean: { bg: '#F0FDF4', text: '#166534', border: '#BBF7D0' },
  denials: { bg: '#FEF2F2', text: '#991B1B', border: '#FECACA' },
  reversals: { bg: '#FFFBEB', text: '#92400E', border: '#FDE68A' },
  imported: { bg: '#F3F4F6', text: '#6B7280', border: '#E5E7EB' },
  open: { bg: '#F5F4FB', text: '#36229E', border: '#E6E1FF' },
  void: { bg: '#F3F4F6', text: '#6B7280', border: '#E5E7EB' },
  uncollectible: { bg: '#FEF2F2', text: '#991B1B', border: '#FECACA' },
};

const severityColors: Record<string, string> = {
  error: '#DC2626',
  warning: '#D97706',
  success: '#16A34A',
  info: '#36229E',
};

export function CellContent({
  column,
  value,
  row,
  onAction,
}: {
  column: ColumnDef;
  value: unknown;
  row: RowData;
  onAction?: (row: RowData) => void;
}) {
  // Safely stringify — guard against objects
  const strValue = typeof value === 'string' || typeof value === 'number' ? String(value) : '';
  switch (column.type) {
    case 'chip': {
      const colors = chipColorMap[strValue] ?? chipColorMap['imported'];
      const label = String(
        row[`${column.key}_label`] ?? row['phase_label'] ?? row['outcome_label'] ?? strValue,
      );
      return (
        <Chip
          label={label}
          size="small"
          sx={{
            bgcolor: colors.bg,
            color: colors.text,
            border: `1px solid ${colors.border}`,
            fontWeight: 500,
            fontSize: '0.6875rem',
            height: 22,
          }}
        />
      );
    }
    case 'patient': {
      const subtitle = row[`${column.key}_subtitle`] as string | undefined;
      return (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
            {strValue}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      );
    }
    case 'action': {
      const severity = String(
        row[`${column.key}_severity`] ??
          (row['what_to_do'] as Record<string, unknown> | undefined)?.severity ??
          '',
      );
      const color = severity ? severityColors[severity] : undefined;
      const rawText =
        typeof value === 'object' && value != null
          ? (value as Record<string, unknown>).text
          : value;
      const displayText = typeof rawText === 'string' ? rawText : strValue;
      return (
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.75rem',
            color: color ?? 'text.primary',
            fontWeight: severity === 'error' ? 500 : 400,
          }}
        >
          {displayText}
        </Typography>
      );
    }
    case 'mono':
      return (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.8125rem',
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {strValue}
        </Typography>
      );
    case 'actionButton':
      return (
        <Button
          size="small"
          variant="outlined"
          onClick={e => {
            e.stopPropagation();
            onAction?.(row);
          }}
          sx={{
            fontSize: '0.6875rem',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: '6px',
            py: 0.25,
            px: 1.5,
          }}
        >
          {strValue}
        </Button>
      );
    case 'issues': {
      const errors = (row['error_count'] as number) ?? 0;
      const warnings = (row['warning_count'] as number) ?? 0;
      if (!errors && !warnings) return null;
      return (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {errors > 0 && (
            <Box
              sx={{
                bgcolor: '#FEF2F2',
                color: '#DC2626',
                px: 0.75,
                py: 0.125,
                borderRadius: '4px',
                fontSize: '0.625rem',
                fontWeight: 600,
              }}
            >
              {errors}e
            </Box>
          )}
          {warnings > 0 && (
            <Box
              sx={{
                bgcolor: '#FFFBEB',
                color: '#D97706',
                px: 0.75,
                py: 0.125,
                borderRadius: '4px',
                fontSize: '0.625rem',
                fontWeight: 600,
              }}
            >
              {warnings}w
            </Box>
          )}
        </Box>
      );
    }
    default:
      return (
        <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.primary' }}>
          {strValue || '—'}
        </Typography>
      );
  }
}

export const DataTable: React.FC<DataTableProps> = ({
  columns,
  rows,
  onRowClick,
  onAction,
  idKey = 'id',
  isFetching = false,
  renderRowEnd,
  rowEndLabel,
  rowEndWidth = '40px',
  selectedIds,
  onSelectionChange,
  isRowSelectable,
  selectionLabel,
  getRowSelectionLabel,
  renderCell,
  rowSx,
  sort,
}) => {
  const hasRowEnd = Boolean(renderRowEnd);
  const selectable = Boolean(selectedIds && onSelectionChange);
  const gridTemplate =
    (selectable ? '36px ' : '') +
    columns.map(c => c.width).join(' ') +
    (hasRowEnd ? ` ${rowEndWidth}` : '');

  const selectableRows = selectable ? rows.filter(r => isRowSelectable?.(r) ?? true) : [];
  const allSelected =
    selectable &&
    selectableRows.length > 0 &&
    selectableRows.every(r => selectedIds?.has(r[idKey] as string) ?? false);
  const someSelected =
    selectable && selectableRows.some(r => selectedIds?.has(r[idKey] as string) ?? false);

  const handleSelectAll = () => {
    if (!onSelectionChange) return;
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(selectableRows.map(r => r[idKey] as string)));
    }
  };

  const handleToggle = (row: RowData) => {
    if (!onSelectionChange || !selectedIds) return;
    if (isRowSelectable && !isRowSelectable(row)) return;
    const rowId = row[idKey] as string;
    const next = new Set(selectedIds);
    if (next.has(rowId)) next.delete(rowId);
    else next.add(rowId);
    onSelectionChange(next);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: '#fff',
        borderRadius: '10px',
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      {/* Fetching overlay */}
      {isFetching && rows.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(255,255,255,0.6)',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '10px',
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}
      {/* Header */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: gridTemplate,
          gap: 1.5,
          px: 2.5,
          py: 1.25,
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {selectable && (
          <Checkbox
            size="small"
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
            onChange={handleSelectAll}
            slotProps={{ input: { 'aria-label': selectionLabel ?? 'Select all rows' } }}
            sx={{ p: 0 }}
          />
        )}
        {columns.map(col => {
          const active = sort?.columnKey === col.key;
          const direction = active ? sort.direction : (col.defaultSortDirection ?? 'asc');
          const headerTextSx = {
            color: 'text.secondary',
            fontSize: '0.6875rem',
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          } as const;

          return (
            <Box
              key={col.key}
              aria-sort={
                col.sortable && sort
                  ? active
                    ? direction === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                  : undefined
              }
              role="columnheader"
              sx={{ minWidth: 0, textAlign: col.align ?? 'left' }}
            >
              {col.sortable && sort ? (
                <TableSortLabel
                  active={active}
                  direction={direction}
                  onClick={() =>
                    sort.onChange(
                      col.key,
                      active ? (sort.direction === 'asc' ? 'desc' : 'asc') : direction,
                    )
                  }
                  sx={{
                    ...headerTextSx,
                    '& .MuiTableSortLabel-icon': { fontSize: '1rem' },
                  }}
                >
                  {col.label}
                </TableSortLabel>
              ) : (
                <Typography variant="subtitle2" sx={headerTextSx}>
                  {col.label}
                </Typography>
              )}
            </Box>
          );
        })}
        {hasRowEnd && (
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: '0.6875rem',
              fontWeight: 600,
              letterSpacing: '0.04em',
              textAlign: 'right',
              textTransform: 'uppercase',
            }}
            variant="subtitle2"
          >
            {rowEndLabel}
          </Typography>
        )}
      </Box>

      {/* Rows */}
      {rows.map((row, idx) => (
        <Box
          key={(row[idKey] as string) ?? idx}
          onClick={() => onRowClick?.(row)}
          sx={{
            display: 'grid',
            gridTemplateColumns: gridTemplate,
            gap: 1.5,
            alignItems: 'center',
            px: 2.5,
            py: 1.25,
            minHeight: 48,
            borderBottom: idx < rows.length - 1 ? '1px solid' : 'none',
            borderColor: 'divider',
            cursor: onRowClick ? 'pointer' : 'default',
            '&:hover': onRowClick ? { bgcolor: 'rgba(54, 34, 158, 0.02)' } : undefined,
            ...(selectedIds?.has(row[idKey] as string)
              ? {
                  bgcolor: 'action.selected',
                  '&:hover': { bgcolor: 'action.selected' },
                }
              : {}),
            ...rowSx?.(row),
          }}
        >
          {selectable && (
            <Checkbox
              size="small"
              checked={selectedIds?.has(row[idKey] as string) ?? false}
              disabled={isRowSelectable ? !isRowSelectable(row) : false}
              onChange={e => {
                e.stopPropagation();
                handleToggle(row);
              }}
              onClick={e => e.stopPropagation()}
              slotProps={{
                input: {
                  'aria-label': getRowSelectionLabel?.(row) ?? 'Select row',
                },
              }}
              sx={{ p: 0 }}
            />
          )}
          {columns.map(col => {
            const customCell = renderCell?.(col, row);
            return (
              <Box
                key={col.key}
                sx={{ textAlign: col.align ?? 'left', overflow: 'hidden', minWidth: 0 }}
              >
                {customCell !== undefined && customCell !== null ? (
                  customCell
                ) : (
                  <CellContent
                    column={col}
                    value={row[col.key] as unknown}
                    row={row}
                    onAction={onAction}
                  />
                )}
              </Box>
            );
          })}
          {renderRowEnd && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: rowEndLabel || rowEndWidth !== '40px' ? 'flex-end' : 'center',
              }}
            >
              {renderRowEnd(row)}
            </Box>
          )}
        </Box>
      ))}

      {rows.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No results
          </Typography>
        </Box>
      )}
    </Box>
  );
};
